/**
 * RecipientPicker
 *
 * Unified entity selector for correspondence recipients.
 * Searches across carriers, medical providers, and police departments.
 * Includes "Add..." option for creating new insurance carriers.
 */

import {
  CheckIcon,
  ChevronDownIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import {
  Building2Icon,
  HeartPulseIcon,
  Loader2Icon,
  PlusIcon,
  SearchIcon,
  ShieldIcon,
} from 'lucide-react';
import React, { useState } from 'react';

import { CreateCarrierModal } from '@/components/carrier-directory/CreateCarrierModal';
import { EditCarrierModal } from '@/components/carrier-directory/EditCarrierModal';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import type { RecipientIdentity, SearchableEntityType } from '../types';
import type { UnifiedRecipientResult } from './useUnifiedRecipientSearch';
import { useUnifiedRecipientSearch } from './useUnifiedRecipientSearch';

interface RecipientPickerProps {
  value: RecipientIdentity | null;
  onChange: (selection: RecipientIdentity | null) => void;
  disabled?: boolean;
  className?: string;
  defaultSearchTerm?: string | null;
}

const ENTITY_ICONS: Record<SearchableEntityType, React.ElementType> = {
  insurance_carrier: Building2Icon,
  medical_provider: HeartPulseIcon,
  police_department: ShieldIcon,
};

const ENTITY_COLORS: Record<
  SearchableEntityType,
  { bg: string; text: string; icon: string }
> = {
  insurance_carrier: {
    bg: 'bg-indigo-500',
    text: 'text-indigo-900',
    icon: 'text-indigo-500',
  },
  medical_provider: {
    bg: 'bg-emerald-500',
    text: 'text-emerald-900',
    icon: 'text-emerald-500',
  },
  police_department: {
    bg: 'bg-amber-500',
    text: 'text-amber-900',
    icon: 'text-amber-500',
  },
};

export const RecipientPicker: React.FC<RecipientPickerProps> = ({
  value,
  onChange,
  disabled,
  className,
  defaultSearchTerm,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateCarrierModalOpen, setIsCreateCarrierModalOpen] =
    useState(false);
  const [isEditCarrierModalOpen, setIsEditCarrierModalOpen] = useState(false);

  const { groupedResults, isFetching, hasQuery } = useUnifiedRecipientSearch({
    query: searchQuery,
    enabled: open,
  });

  const handleSelect = (result: UnifiedRecipientResult) => {
    onChange({
      entityId: result.id,
      displayName: result.name,
      entityType: result.type,
    });
    setOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearchQuery('');
    } else if (!value && defaultSearchTerm && !searchQuery) {
      // Auto-populate search query if opening with no selection
      setSearchQuery(defaultSearchTerm);
    }
  };

  const handleAddCarrier = () => {
    setOpen(false);
    setIsCreateCarrierModalOpen(true);
  };

  // Check if selected recipient is an insurance carrier (for edit button)
  const isInsuranceCarrier = value?.entityType === 'insurance_carrier';

  const ValueIcon = value ? ENTITY_ICONS[value.entityType] : SearchIcon;
  const valueColor = value ? ENTITY_COLORS[value.entityType] : null;

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'group w-full flex items-center gap-2 rounded-lg px-2.5 py-1.5',
              'bg-white border border-zinc-200/80 text-left',
              'transition-all duration-150',
              'hover:border-zinc-300 hover:bg-zinc-50/50',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-zinc-400/30',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              open && 'border-zinc-300 bg-zinc-50/50 ring-2 ring-zinc-400/20',
              className,
            )}
          >
            <div
              className={cn(
                'flex items-center justify-center h-6 w-6 rounded-md flex-shrink-0',
                value
                  ? `${valueColor?.bg} text-white`
                  : 'bg-zinc-100 text-zinc-400',
              )}
            >
              <ValueIcon className="h-3 w-3" />
            </div>

            <div className="flex-1 min-w-0">
              {value ? (
                <div className="truncate text-[12px] font-medium text-zinc-900">
                  {value.displayName}
                </div>
              ) : (
                <div className="text-[12px] text-zinc-400">
                  Search recipients...
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {value && !disabled && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleClear}
                  onKeyDown={(e) =>
                    e.key === 'Enter' &&
                    handleClear(e as unknown as React.MouseEvent)
                  }
                  className="p-1 rounded-full text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors"
                >
                  <XMarkIcon className="h-4 w-4" />
                </span>
              )}
              <ChevronDownIcon
                className={cn(
                  'h-4 w-4 text-zinc-400 transition-transform duration-150',
                  open && 'rotate-180',
                )}
              />
            </div>
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl border-zinc-200/80 shadow-lg overflow-hidden"
          align="start"
          sideOffset={4}
        >
          <Command className="bg-white" shouldFilter={false}>
            <div className="px-3 py-2 border-b border-zinc-100">
              <CommandInput
                placeholder="Search carriers, providers, police..."
                className="h-8 text-[13px] border-0 focus:ring-0 p-0 placeholder:text-zinc-400"
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
            </div>
            <CommandList className="max-h-[320px] overflow-auto">
              <CommandEmpty className="py-6 text-center">
                {(() => {
                  if (isFetching) {
                    return (
                      <Loader2Icon className="h-4 w-4 animate-spin text-zinc-400 mx-auto" />
                    );
                  }
                  if (!hasQuery) {
                    return (
                      <span className="text-[13px] text-zinc-500">
                        Type to search directories
                      </span>
                    );
                  }
                  return (
                    <span className="text-[13px] text-zinc-500">
                      No results found
                    </span>
                  );
                })()}
              </CommandEmpty>

              {groupedResults.map((group) => {
                const colors = ENTITY_COLORS[group.type];
                const GroupIcon = ENTITY_ICONS[group.type];

                return (
                  <CommandGroup
                    key={group.type}
                    heading={
                      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 px-1">
                        <GroupIcon className={cn('h-3 w-3', colors.icon)} />
                        {group.label}
                      </span>
                    }
                    className="p-1"
                  >
                    {group.items.map((result) => {
                      const isSelected = value?.entityId === result.id;
                      return (
                        <CommandItem
                          key={`${result.type}-${result.id}`}
                          value={`${result.type}-${result.id}-${result.name}`}
                          onSelect={() => handleSelect(result)}
                          className={cn(
                            'flex items-center justify-between rounded-lg px-2.5 py-2 cursor-pointer',
                            'text-[13px] transition-colors',
                            isSelected && 'bg-zinc-100',
                          )}
                        >
                          <div className="flex flex-col min-w-0">
                            <span
                              className={cn(
                                'truncate',
                                isSelected
                                  ? `${colors.text} font-medium`
                                  : 'text-zinc-700',
                              )}
                            >
                              {result.name}
                            </span>
                            {result.subtitle && (
                              <span className="text-[11px] text-zinc-400 truncate">
                                {result.subtitle}
                              </span>
                            )}
                          </div>
                          {isSelected && (
                            <CheckIcon
                              className={cn(
                                'h-4 w-4 flex-shrink-0',
                                colors.icon,
                              )}
                            />
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                );
              })}

              {/* Add new carrier option */}
              <CommandSeparator className="my-1" />
              <CommandGroup className="p-1">
                <CommandItem
                  value="__add_carrier__"
                  onSelect={handleAddCarrier}
                  className="flex items-center gap-2 rounded-lg px-2.5 py-2 cursor-pointer text-[13px] text-zinc-600 hover:text-indigo-600"
                >
                  <div className="flex items-center justify-center h-6 w-6 rounded-md bg-zinc-100">
                    <PlusIcon className="h-3.5 w-3.5" />
                  </div>
                  Add Insurance Carrier...
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>

        {/* Create Carrier Modal */}
        <CreateCarrierModal
          open={isCreateCarrierModalOpen}
          onClose={() => setIsCreateCarrierModalOpen(false)}
          isHealthCarrier
        />

        {/* Edit Carrier Modal */}
        {value?.entityId && isInsuranceCarrier && (
          <EditCarrierModal
            open={isEditCarrierModalOpen}
            onClose={() => setIsEditCarrierModalOpen(false)}
            carrierId={value.entityId}
            onSaved={(newName) => {
              // Update the display name if it changed
              onChange({ ...value, displayName: newName });
            }}
          />
        )}
      </Popover>

      {/* Edit carrier link - shown below picker for insurance carriers */}
      {isInsuranceCarrier && value && (
        <button
          type="button"
          onClick={() => setIsEditCarrierModalOpen(true)}
          className="mt-1.5 text-[11px] text-indigo-600 hover:text-indigo-700 hover:underline transition-colors"
        >
          Update Carrier
        </button>
      )}
    </>
  );
};
